import cx from "classnames";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Icon from "library/components/Icon";
import {
  type Tournament,
  useTournaments,
} from "hooks/firestore/v2/betting/useTournaments";
import { TitleIcon } from "components/TitleIcon";
import { useHub } from "hooks/graphql/useHubs";
import { Dialog } from "components/Dialog";
import { Input } from "components/Input";
import type { Title } from "hooks/graphql/useTitles";
import { ScrollArea } from "library";
import { useBettingFilters } from "./hooks/useBettingFilters";
import { Button } from "components/Button";
import { useDebounce } from "usehooks-ts";

import * as styles from "./TournamentFilter.module.scss";
import { AnimatePresence, motion } from "framer-motion";

type TournamentFilterProps = {
  hubSlug: string;
  sports: string[];
  // this setPage is different to the page value in filters, although they
  // essentially are the same thing, listening only to the page value that comes
  // from useRoute causes a number of unnecessary renders, decided that it was
  // an unncessary rabbit hole for now
  setPage: (page: number) => void;
};

type TournamentFilterDialogProps = {
  open: boolean;
  onClose: () => void;
  onSelect: (selected: string) => void;
  onClear: () => void;
  tournaments: Tournament[];
  selectedTournaments: string[];
  titles: Title[];
};

const TournamentToggleGroupItem = ({
  tournamentId,
  tournamentName,
  isActive,
  title,
  onSelect,
}: {
  tournamentId: string;
  tournamentName: string;
  isActive: boolean;
  title: Title;
  onSelect: (id: string) => void;
}) => {
  return (
    <div
      onClick={() => onSelect(tournamentId)}
      className={cx(styles.toggleGroupItem, {
        [styles.active]: isActive,
      })}
    >
      <TitleIcon className={styles.titleIcon} title={title} svg />
      {tournamentName}
    </div>
  );
};

const TournamentFilterDialog = ({
  open,
  onClose,
  onSelect,
  onClear,
  tournaments,
  selectedTournaments,
  titles,
}: TournamentFilterDialogProps) => {
  const [searchValue, setSearchValue] = useState("");

  const debouncedSearchValue = useDebounce(searchValue, 200);

  const filteredTournaments = useMemo(() => {
    if (!debouncedSearchValue) return tournaments;

    return tournaments.filter((tournament) => {
      return tournament.name
        .toLowerCase()
        .includes(debouncedSearchValue.toLowerCase());
    });
  }, [tournaments, debouncedSearchValue]);

  return (
    <Dialog
      isOpen={open}
      onClose={onClose}
      className={styles.tournamentFilterDialog}
      hideHeader
    >
      <div className={styles.dialogHeader}>
        <Input
          wrapperClassName={styles.dialogInputWrapper}
          inputClassName={styles.dialogInput}
          placeholder={`Search ${tournaments.length} Events`}
          onChange={(e) => setSearchValue(e.target.value)}
          value={searchValue}
          autoFocus
          noSpacing
        />
      </div>

      <ScrollArea>
        <div className={styles.dialogToggleGroup}>
          {filteredTournaments.length > 0 ? (
            filteredTournaments.map((tournament) => {
              const title = titles.find(
                (title) => title.slug === tournament.sport.toLowerCase(),
              );
              return (
                <TournamentToggleGroupItem
                  title={title}
                  isActive={selectedTournaments.includes(tournament.id)}
                  tournamentId={tournament.id}
                  tournamentName={tournament.name}
                  key={tournament.id}
                  onSelect={onSelect}
                />
              );
            })
          ) : (
            <p>No tournaments found matching "{debouncedSearchValue}"</p>
          )}
        </div>
      </ScrollArea>

      <div className={styles.dialogFooter}>
        <Button
          size="sm"
          className={cx(styles.footerButton, styles.back)}
          onClick={onClose}
        >
          <Icon outerClassName={styles.footerButtonIcon} type="arrow-left" />
          Back
        </Button>
        {selectedTournaments.length > 0 ? (
          <Button
            size="sm"
            variant="danger"
            className={cx(styles.footerButton, styles.clear)}
            onClick={onClear}
          >
            <Icon outerClassName={styles.footerButtonIcon} type="close" />
            Clear Filter
          </Button>
        ) : null}
      </div>
    </Dialog>
  );
};

const TournamentFilter = ({
  sports,
  setPage,
  hubSlug,
}: TournamentFilterProps) => {
  const tournamentFilterRef = useRef<HTMLDivElement>(null);
  const toggleGroupRef = useRef<HTMLDivElement>(null);

  const [filters, setFilters] = useBettingFilters({
    page: 1,
    tab: "next",
  });

  const { tournaments } = useTournaments(sports, filters.tab);

  const [dialogOpen, setDialogOpen] = useState(false);
  // whether the tournaments displayed exceed the width of the container they
  // are in, if so we show a trigger that opens the tournament filter dialog
  const [overflowing, setOverflowing] = useState(false);

  const hub = useHub(hubSlug);
  const titles = hub?.titles ?? [];

  const selectedTournaments = filters.tour ?? [];
  const noTournamentsSelected = selectedTournaments.length === 0;

  // this determines if we show the "more tournaments" button or not, if the
  // content isn't scrollable then the user already has access to all of the
  // filterable tournaments. We subtract 44 because that is the width of the
  // button that will show the expanded tournaments filters dialog
  useEffect(() => {
    if (tournaments?.length === 0) {
      setOverflowing(false);
    }

    if (toggleGroupRef.current && tournamentFilterRef.current) {
      const isOverflowing =
        toggleGroupRef.current.scrollWidth >
        tournamentFilterRef.current.clientWidth - 44;

      setOverflowing(isOverflowing);
    }
  }, [tournaments, toggleGroupRef.current, tournamentFilterRef.current]);

  const sortedTournaments = useMemo(() => {
    if (selectedTournaments.length === 0) return tournaments || [];

    const sortedTournaments = [...(tournaments || [])].sort((a, b) => {
      const aIsSelected = selectedTournaments.includes(a.id);
      const bIsSelected = selectedTournaments.includes(b.id);

      return (bIsSelected ? 1 : 0) - (aIsSelected ? 1 : 0);
    });

    return sortedTournaments;
  }, [tournaments, selectedTournaments]);

  // we want to restrict the amount of tournaments that we render in the toggle
  // group because the container is cut off, so there isn't much point looping
  // through 100 tournaments if the user can only see like 4
  const firstTenTournaments = sortedTournaments.slice(0, 8);

  const handleTournamentSelect = (id: string) => {
    let newTournaments: string[] = [...selectedTournaments];
    if (selectedTournaments.includes(id)) {
      newTournaments = selectedTournaments.filter(
        (selectedId) => selectedId !== id,
      );
    } else {
      newTournaments.push(id);
    }

    setPage(1);
    setFilters({
      tour: newTournaments,
      page: 1,
    });
  };

  const handleClearSelected = () => {
    setPage(1);
    setFilters({
      tour: undefined,
      page: 1,
    });
  };

  return (
    <AnimatePresence>
      {tournaments && tournaments.length > 1 ? (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "6rem", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
        >
          <div className={styles.tournamentFilter} ref={tournamentFilterRef}>
            <div
              className={cx(styles.filterButton, {
                [styles.active]: noTournamentsSelected,
              })}
              onClick={handleClearSelected}
            >
              <Icon
                className={cx(styles.filterIcon, {
                  [styles.all]: noTournamentsSelected,
                })}
                type={noTournamentsSelected ? "all" : "close"}
              />
            </div>

            <div className={styles.toggleGroup} ref={toggleGroupRef}>
              <AnimatePresence mode="popLayout">
                {firstTenTournaments.map((tournament) => {
                  const title = titles.find(
                    (title) => title.slug === tournament.sport.toLowerCase(),
                  );
                  return (
                    <motion.div
                      key={tournament.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <TournamentToggleGroupItem
                        title={title}
                        isActive={selectedTournaments.includes(tournament.id)}
                        tournamentId={tournament.id}
                        tournamentName={tournament.name}
                        key={tournament.id}
                        onSelect={handleTournamentSelect}
                      />
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>

            <AnimatePresence>
              {overflowing && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div className={styles.dialogTrigger}>
                    <button
                      className={cx(styles.dialogTriggerButton, {
                        [styles.active]: !noTournamentsSelected,
                      })}
                      onClick={() => setDialogOpen(true)}
                    >
                      {sortedTournaments.length}
                      <Icon
                        outerClassName={styles.triggerArrowIconWrapper}
                        className={styles.triggerArrowIcon}
                        type="arrow-right"
                      />
                    </button>
                  </div>

                  <TournamentFilterDialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    onClear={() => setFilters({ tour: undefined, page: 1 })}
                    tournaments={sortedTournaments}
                    selectedTournaments={selectedTournaments}
                    titles={titles}
                    onSelect={handleTournamentSelect}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};
TournamentFilter.displayName = "TournamentFilter";

export { TournamentFilter };
